import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes, FaBoxOpen, FaLayerGroup, FaFileAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/InventoryProducts.css';

const CREATE_ITEM = `
  mutation CreateItem($createItemInput: CreateItemInput!) {
    createItem(createItemInput: $createItemInput) {
      id
      name
      code
    }
  }
`;

const UPDATE_ITEM = `
  mutation UpdateItem($updateItemInput: UpdateItemInput!) {
    updateItem(updateItemInput: $updateItemInput) {
      id
      name
      code
    }
  }
`;

const FIND_ITEMS = `
  query FindItemsForProductForm {
    findAllItem {
      id name code category supplier preferredSupplier
    }
  }
`;

const CATEGORIES = [
  'Chemical',
  'PPE',
  'Consumable',
  'Tool',
  'Equipment',
  'Uniform',
  'Device',
  'Vehicle',
];

const UNITS = ['Each', 'Litre', 'Box', 'Roll', 'Pair', 'Kg', 'Pack', 'Carton'];

const SKU_PREFIX = {
  Chemical: 'CHEM',
  PPE: 'PPE',
  Consumable: 'CONS',
  Tool: 'TOOL',
  Equipment: 'EQPT',
  Uniform: 'UNIF',
  Device: 'DEV',
  Vehicle: 'VEH',
};

const DEFAULT_SUPPLIERS = [
  'ChemSupply Australia',
  'Bunzl Cleaning',
  'Officeworks',
  'Blackwoods',
];

function nextSkuPreview(items, category) {
  const prefix = SKU_PREFIX[category] || 'IT';
  let max = 0;
  (items || []).forEach((item) => {
    const code = String(item?.code || '');
    if (!code.toUpperCase().startsWith(prefix)) return;
    const m = code.match(/(\d+)$/);
    if (m) max = Math.max(max, parseInt(m[1], 10) || 0);
  });
  return `${prefix}${String(max + 1).padStart(3, '0')}`;
}

function normalizeSku(code, category) {
  const prefix = SKU_PREFIX[category] || 'IT';
  const digits = String(code || '').match(/(\d+)$/)?.[1];
  if (!digits) return code || '';
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n)) return code || '';
  return `${prefix}${String(n).padStart(3, '0')}`;
}

function optionalNumber(value) {
  if (value === '' || value == null) return undefined;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
}

export default function ItemDescriptionForm({
  onClose,
  onItemAdded,
  editData = null,
}) {
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    unit: '',
    perItemCost: '',
    supplier: '',
    quantity: '',
    reorderLevel: '',
    maxStockLevel: '',
    preferredSupplier: '',
    description: '',
    hasExpiry: false,
    expiryDate: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    graphqlRequest(FIND_ITEMS)
      .then((data) => setItems(Array.isArray(data?.findAllItem) ? data.findAllItem : []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (!editData) return;
    setFormData({
      name: editData.name || '',
      category: editData.category || '',
      unit: editData.unit || '',
      perItemCost: editData.perItemCost?.toString() || '',
      supplier: editData.supplier || '',
      quantity: (
        editData.available_quantity ??
        editData.onHand ??
        editData.quantity ??
        ''
      ).toString(),
      reorderLevel: String(editData.reorderLevel ?? ''),
      maxStockLevel: String(editData.maxStockLevel ?? ''),
      preferredSupplier: editData.preferredSupplier || editData.supplier || '',
      description: editData.description || '',
      hasExpiry: Boolean(editData.expiryDate),
      expiryDate: editData.expiryDate
        ? String(editData.expiryDate).slice(0, 10)
        : '',
    });
  }, [editData]);

  const skuPreview = useMemo(() => {
    const code = editData?.code;
    const cat = editData?.category || formData.category;
    if (code) return normalizeSku(code, cat);
    return nextSkuPreview(items, formData.category);
  }, [editData, items, formData.category]);

  const supplierOptions = useMemo(() => {
    const fromItems = (items || [])
      .flatMap((i) => [i.supplier, i.preferredSupplier])
      .filter(Boolean);
    return Array.from(new Set([...DEFAULT_SUPPLIERS, ...fromItems])).sort();
  }, [items]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const payload = () => ({
    name: formData.name.trim(),
    quantity: parseFloat(formData.quantity),
    perItemCost: optionalNumber(formData.perItemCost),
    description: formData.description.trim() || undefined,
    expiryDate:
      formData.hasExpiry && formData.expiryDate
        ? formData.expiryDate
        : undefined,
    category: formData.category || undefined,
    unit: formData.unit || undefined,
    reorderLevel: optionalNumber(formData.reorderLevel),
    maxStockLevel: optionalNumber(formData.maxStockLevel),
    supplier: formData.supplier.trim() || undefined,
    preferredSupplier:
      (formData.preferredSupplier || formData.supplier).trim() || undefined,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (!formData.category) {
      toast.error('Category is required');
      return;
    }
    if (!formData.unit) {
      toast.error('Unit of measure is required');
      return;
    }
    if (formData.perItemCost === '' || parseFloat(formData.perItemCost) < 0) {
      toast.error('Per unit cost is required');
      return;
    }
    if (formData.quantity === '' || parseFloat(formData.quantity) < 0) {
      toast.error('Initial quantity is required');
      return;
    }
    if (formData.reorderLevel === '' || parseFloat(formData.reorderLevel) < 0) {
      toast.error('Reorder level is required');
      return;
    }

    setLoading(true);
    try {
      const data = payload();
      const legacy = {
        name: data.name,
        quantity: data.quantity,
        perItemCost: data.perItemCost,
        description: data.description,
        expiryDate: data.expiryDate,
        category: data.category,
        unit: data.unit,
        reorderLevel: data.reorderLevel,
      };
      const run = async (body) => {
        if (editData) {
          return graphqlRequest(UPDATE_ITEM, {
            updateItemInput: { id: editData.id, ...body },
          });
        }
        return graphqlRequest(CREATE_ITEM, { createItemInput: body });
      };
      try {
        await run(data);
      } catch (err) {
        const msg = String(err?.message || '').toLowerCase();
        if (
          msg.includes('maxstock') ||
          msg.includes('supplier') ||
          msg.includes('preferred')
        ) {
          await run(legacy);
        } else {
          throw err;
        }
      }
      toast.success(editData ? 'Product updated' : 'Product added');
      if (onItemAdded) onItemAdded();
      onClose();
    } catch (error) {
      toast.error(error?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ap-overlay" onClick={onClose}>
      <div
        className="ap-modal"
        role="dialog"
        aria-labelledby="ap-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ap-header">
          <h2 id="ap-title">{editData ? 'Edit Product' : 'Add Product'}</h2>
          <button type="button" className="ap-close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </header>

        <form className="ap-form" onSubmit={handleSubmit}>
          <section className="ap-section">
            <h3>
              <FaBoxOpen /> Product Information
            </h3>
            <div className="ap-grid">
              <label className="ap-field">
                <span>
                  Product Name <em>*</em>
                </span>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Disinfectant Concentrate"
                  required
                />
              </label>
              <label className="ap-field">
                <span>SKU (Auto-generated)</span>
                <input value={skuPreview} readOnly className="is-readonly" />
              </label>
              <label className="ap-field">
                <span>
                  Category <em>*</em>
                </span>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select category</option>
                  {CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label className="ap-field">
                <span>
                  Unit of Measure <em>*</em>
                </span>
                <select
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select unit</option>
                  {UNITS.map((u) => (
                    <option key={u}>{u}</option>
                  ))}
                </select>
              </label>
              <label className="ap-field">
                <span>
                  Per Unit Cost <em>*</em>
                </span>
                <input
                  type="number"
                  name="perItemCost"
                  min="0"
                  step="0.01"
                  value={formData.perItemCost}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                />
              </label>
              <label className="ap-field">
                <span>Supplier</span>
                <input
                  name="supplier"
                  list="ap-suppliers"
                  value={formData.supplier}
                  onChange={handleChange}
                  placeholder="Select or type supplier"
                />
              </label>
            </div>
          </section>

          <section className="ap-section">
            <h3>
              <FaLayerGroup /> Stock Settings
            </h3>
            <div className="ap-grid">
              <label className="ap-field">
                <span>
                  {editData ? 'Current Quantity' : 'Initial Quantity'} <em>*</em>
                </span>
                <input
                  type="number"
                  name="quantity"
                  min="0"
                  step="0.01"
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder="0"
                  required
                />
              </label>
              <label className="ap-field">
                <span>
                  Reorder Level <em>*</em>
                </span>
                <input
                  type="number"
                  name="reorderLevel"
                  min="0"
                  step="1"
                  value={formData.reorderLevel}
                  onChange={handleChange}
                  placeholder="0"
                  required
                />
              </label>
              <label className="ap-field">
                <span>Max Stock Level</span>
                <input
                  type="number"
                  name="maxStockLevel"
                  min="0"
                  step="1"
                  value={formData.maxStockLevel}
                  onChange={handleChange}
                  placeholder="0"
                />
              </label>
              <label className="ap-field">
                <span>Preferred Supplier</span>
                <input
                  name="preferredSupplier"
                  list="ap-suppliers"
                  value={formData.preferredSupplier}
                  onChange={handleChange}
                  placeholder="Select preferred supplier"
                />
              </label>
            </div>
          </section>

          <section className="ap-section">
            <h3>
              <FaFileAlt /> Additional Details
            </h3>
            <label className="ap-field ap-field--full">
              <span>Description</span>
              <textarea
                name="description"
                rows={4}
                value={formData.description}
                onChange={handleChange}
                placeholder="Optional product description"
              />
            </label>
            <label className="ap-check">
              <input
                type="checkbox"
                name="hasExpiry"
                checked={formData.hasExpiry}
                onChange={handleChange}
              />
              Expiry Applicable
            </label>
            {formData.hasExpiry ? (
              <label className="ap-field ap-field--half">
                <span>Expiry Date</span>
                <input
                  type="date"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleChange}
                />
              </label>
            ) : null}
          </section>

          <datalist id="ap-suppliers">
            {supplierOptions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>

          <div className="ap-footer">
            <button type="button" className="ap-btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="ap-btn ap-btn--primary" disabled={loading}>
              {loading ? 'Saving…' : editData ? 'Update Product' : 'Save Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
